import * as React from 'react';
import { Formik, Field } from 'formik';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';

import { buildNavItems, useApi, useAuth, useCerebroNav } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { FormikUtils, HtmlUtils } from '../../shared/utils';
import { LoanApplicationTask, UpdateLoanApplicationTaskDto, UpdateLoanApplicationTaskType } from '../../shared/models';

import {
	CerebroDrawer,
	CerebroForm,
	CerebroFileDragAndDrop,
	CerebroPrimaryButton,
	CerebroHeading,
	CerebroText,
} from '../../shared/components';
import { getApiResponseData } from '../../shared/utils/axios';

export const UploadDocumentDrawer: React.FC<UploadDocumentDrawerProps> = ({
	open = false,
	onClose,
	document: requiredDocument,
}) => {
	const api = useApi();
	const nav = useCerebroNav();
	const client = useQueryClient();
	const { currentUser } = useAuth();
	const { guid } = useParams<'guid'>();

	const snackbar = useSnackbar();
	const queryClient = useQueryClient();

	const ref = React.useRef<HTMLDivElement>(null);

	const uploadMutation = useMutation(
		async ({ values }: RequiredDocumentMutationValues) => {
			let filesUpdate: LoanApplicationTask | null = null;
			let taskUpdate: LoanApplicationTask | null = null;

			const uploads = values.uploads;

			if (uploads && uploads.length > 0) {
				let formData = new FormData();

				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});

				const { data, error } = await getApiResponseData<LoanApplicationTask>(
					api.post(CerebroApiEndpoints.RequiredDocuments.Upload(guid, requiredDocument.guid), formData, {
						headers: { 'Content-Type': 'multipart/form-data' },
					})
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				filesUpdate = data;
			}

			const documentPatch: UpdateLoanApplicationTaskDto = {
				updateType: values.updateType,
				notApplicable: false,
				uploads: [],
			};

			const { data, error } = await getApiResponseData<LoanApplicationTask>(
				api.patch(CerebroApiEndpoints.RequiredDocuments.Specific(guid, requiredDocument.guid), documentPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			taskUpdate = data;

			return { filesUpdate, taskUpdate };
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QueryKeys.RequiredDocuments.Root(guid)).finally(() => {
					const navState = buildNavItems(
						currentUser,
						client.getQueryData(QueryKeys.Allocation(guid)),
						client.getQueryData(QueryKeys.Deal.Root(guid)),
						location.pathname,
						client.getQueryData(QueryKeys.Questions.LoanApp),
						client.getQueryData(QueryKeys.Product(guid)),
						client.getQueryData(QueryKeys.RequiredDocuments.Root(guid)),
						client.getQueryData(QueryKeys.Integrations.Config())
					);
					nav.setState(navState);
				});
				snackbar.enqueueSnackbar('Required Document saved.', { variant: 'success' });

				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<CerebroHeading size="h2" component="h3" gutterBottom>
				Upload {requiredDocument.documents.length > 0 ? 'Additional' : ''} Documents
			</CerebroHeading>
			<CerebroText variant="small" gutterBottom>
				Upload documents to support your loan application.
			</CerebroText>
			<Formik
				enableReinitialize
				onSubmit={FormikUtils.MutateOnSubmit(uploadMutation.mutate)}
				initialValues={{
					updateType: 'Upload' as UpdateLoanApplicationTaskType,
					notApplicable: requiredDocument.notApplicable,
					uploads: [] as File[],
				}}
			>
				{({ isSubmitting, handleSubmit }) => (
					<CerebroForm
						flatIndicator
						encType="multipart/form-data"
						onSubmit={handleSubmit}
						sx={{ marginTop: 3, paddingRight: 3 }}
						scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
					>
						<CerebroHeading size="h2" component="h2" gutterBottom>
							{requiredDocument.applicationTaskTitle}
						</CerebroHeading>
						{requiredDocument.summary && <CerebroText>{requiredDocument.summary}</CerebroText>}

						<Field
							as={CerebroFileDragAndDrop}
							name="uploads"
							sx={{ marginY: 3, width: '100%' }}
							onlyPdf={false}
							multiple={true}
						/>

						<CerebroPrimaryButton loading={isSubmitting} type="submit" sx={{ marginTop: 3 }}>
							Save Details
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

interface UploadDocumentDrawerProps {
	open?: boolean;
	onClose: () => void;

	document: LoanApplicationTask;
}

type RequiredDocumentMutationValues = FormikUtils.MutateOnSubmitVariables<LoanApplicationTaskValues>;

interface LoanApplicationTaskValues {
	updateType: UpdateLoanApplicationTaskType;
	notApplicable: boolean;
	uploads: File[];
}
